/**
 * 사업 연도별 이력
 */
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';

export const PROJECT_HISTORY_STORAGE_PREFIX = 'budgetProjectHistory';

export function createProjectUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `PU-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildProjectPathCode(row, allRows = []) {
  if (!row?.projectCode) return '';
  const bt = row.budgetType || '';
  const byKey = new Map(
    (allRows || []).map((r) => [`${r.budgetType || ''}::${r.projectCode}`, r])
  );
  const parts = [];
  let cur = row;
  let guard = 0;
  while (cur && guard++ < 24) {
    parts.unshift(String(cur.projectCode));
    if (!cur.parentCode) break;
    cur = byKey.get(`${bt}::${cur.parentCode}`) || null;
  }
  return parts.join('-');
}

export function ensureProjectsIdentity(rows = []) {
  const withUid = (rows || []).map((r) => ({
    ...r,
    projectUid: r.projectUid || createProjectUid(),
  }));
  return withUid.map((r) => ({
    ...r,
    pathCode: r.pathCode || buildProjectPathCode(r, withUid),
  }));
}

export function projectHistoryStorageKey(tenantCode, projectUid) {
  return [PROJECT_HISTORY_STORAGE_PREFIX, tenantCode || '', projectUid || ''].join('|');
}

export function loadProjectHistory(tenantCode, projectUid) {
  if (!projectUid) return [];
  try {
    const raw = localStorage.getItem(projectHistoryStorageKey(tenantCode, projectUid));
    if (!raw) return [];
    const data = JSON.parse(raw);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return rows.sort((a, b) => Number(b.fiscalYear) - Number(a.fiscalYear));
  } catch {
    return [];
  }
}

export function saveProjectHistory(tenantCode, projectUid, rows) {
  if (!projectUid) return;
  const key = projectHistoryStorageKey(tenantCode, projectUid);
  const value = {
    tenantCode,
    projectUid,
    rows: rows || [],
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(key, JSON.stringify(value));
  if (isBdApiEnabled()) {
    enqueuePersist(key, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.PROJECT_HIST,
        rowUid: projectUid,
        payload: value,
        params: pocParams({ tenantCode }),
      })
    );
  }
}

export function upsertCurrentYearProjectHistory(tenantCode, projectRow, fiscalYear) {
  if (!projectRow?.projectUid) return [];
  const fy = String(fiscalYear || '');
  const prev = loadProjectHistory(tenantCode, projectRow.projectUid);
  const snap = {
    fiscalYear: fy,
    seqNo: 0,
    budgetType: projectRow.budgetType || '',
    levelName: projectRow.levelName || '',
    projectCode: projectRow.projectCode || '',
    projectName: projectRow.projectName || '',
    projectUid: projectRow.projectUid,
    pathCode: projectRow.pathCode || '',
    inputYn: projectRow.inputYn || 'Y',
    useYn: projectRow.useYn || 'Y',
    remark: projectRow.remark || '',
  };
  const idx = prev.findIndex(
    (r) => String(r.fiscalYear) === fy && (r.budgetType || '') === (snap.budgetType || '')
  );
  let next;
  if (idx >= 0) {
    next = [...prev];
    next[idx] = { ...next[idx], ...snap };
  } else {
    next = [snap, ...prev];
  }
  next.sort((a, b) => Number(b.fiscalYear) - Number(a.fiscalYear));
  saveProjectHistory(tenantCode, projectRow.projectUid, next);
  return next;
}

export function ensureSampleProjectHistory(tenantCode, projectRow, currentFiscalYear, yearsBack = 3) {
  if (!projectRow?.projectUid) return [];
  let list = upsertCurrentYearProjectHistory(tenantCode, projectRow, String(currentFiscalYear));
  const fy = Number(currentFiscalYear);
  if (!Number.isFinite(fy)) return list;

  for (let i = 1; i <= yearsBack; i += 1) {
    const y = String(fy - i);
    const exists = list.some(
      (r) => String(r.fiscalYear) === y && (r.budgetType || '') === (projectRow.budgetType || '')
    );
    if (exists) continue;
    list.push({
      fiscalYear: y,
      seqNo: 0,
      budgetType: projectRow.budgetType || '',
      levelName: projectRow.levelName || '',
      projectCode: projectRow.projectCode || '',
      projectName: projectRow.projectName || '',
      projectUid: projectRow.projectUid,
      pathCode: projectRow.pathCode || '',
      inputYn: 'Y',
      useYn: 'Y',
      remark: `${y}년 사업 스냅샷(샘플)`,
    });
  }
  list.sort((a, b) => Number(b.fiscalYear) - Number(a.fiscalYear));
  saveProjectHistory(tenantCode, projectRow.projectUid, list);
  return list;
}

export function upsertHistoryForProjects(tenantCode, projectRows, fiscalYear) {
  (projectRows || []).forEach((r) => {
    if (r?.projectUid && r?.projectCode) {
      upsertCurrentYearProjectHistory(tenantCode, r, fiscalYear);
    }
  });
}
